import {
    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR,
    WIDGET_VERSION,
} from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import {
    RewardsHubB2bWidgetDefinition,
    RewardsHubWidgetDefinition,
    RewardsHubWidgetV2Definition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { parseOptionalBooleanDataAttr } from "@ui-utils/Injector/utils";

import type RewardsHubB2bWidget from "./b2b/RewardHubB2bWidget";
import type RewardsHubWidget from "./v1/RewardsHubWidget";
import type RewardsHubWidgetV2 from "./v2/RewardsHubWidgetV2";

interface RewardsHubWidgetInjectorConfig extends RewardsWidgetInjectorConfig {
    isCustomerB2b: boolean;
}
class RewardsHubWidgetInjector {
    constructor(private readonly config: RewardsHubWidgetInjectorConfig) {}

    start(): void {
        this.config.logger.debug("RewardsHubWidgetInjector start", {
            placeholderSelector: this.config.placeholderSelector,
        });

        this.config.mutationObserverInjector
            .register({
                targetSelector: this.config.placeholderSelector,
            })
            .subscribe((placeholder) => {
                this.config.logger?.debug(
                    "RewardsHubWidgetInjector found placeholder",
                    {
                        placeholder,
                    }
                );

                const widgetVersion = WIDGET_VERSION.V1;
                let widget:
                    | RewardsHubWidget
                    | RewardsHubWidgetV2
                    | RewardsHubB2bWidget;
                let widgetName: REWARDS_WIDGET_NAMES;
                const commonWidgetConfig = {
                    target: placeholder,
                    isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                        placeholder,
                        WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                    ),
                    translations: {
                        pointsNamespace:
                            TRANSLATION_NAMESPACES.commonCriticalPoints,
                    },
                    diResolverService: this.config.diResolverService,
                };

                void (async () => {
                    if (widgetVersion === WIDGET_VERSION.V1) {
                        if (this.config.isCustomerB2b) {
                            const RewardsHubB2bWidget =
                                await this.config.dynamicImportManager.importDefault(
                                    RewardsHubB2bWidgetDefinition,
                                    true
                                );

                            widgetName =
                                REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET_V2;
                            widget = new RewardsHubB2bWidget(
                                {
                                    ...commonWidgetConfig,
                                    widgetName,
                                    translations: {
                                        ...commonWidgetConfig.translations,
                                        widgetNamespace:
                                            TRANSLATION_NAMESPACES.hubWidget,
                                        criticalWidgetNamespace:
                                            TRANSLATION_NAMESPACES.criticalPopUpWidget,
                                    },
                                },
                                this.config.logger
                            );
                        } else {
                            const RewardsHubWidget =
                                await this.config.dynamicImportManager.importDefault(
                                    RewardsHubWidgetDefinition,
                                    true
                                );

                            widgetName =
                                REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET;
                            widget = new RewardsHubWidget(
                                {
                                    ...commonWidgetConfig,
                                    widgetName,
                                    translations: {
                                        ...commonWidgetConfig.translations,
                                        widgetNamespace:
                                            TRANSLATION_NAMESPACES.hubWidget,
                                        criticalWidgetNamespace:
                                            TRANSLATION_NAMESPACES.criticalPopUpWidget,
                                    },
                                },
                                this.config.logger
                            );
                        }
                    } else {
                        const RewardsHubWidgetV2 =
                            await this.config.dynamicImportManager.importDefault(
                                RewardsHubWidgetV2Definition,
                                true
                            );

                        widgetName = REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET_V2;
                        widget = new RewardsHubWidgetV2(
                            {
                                ...commonWidgetConfig,
                                widgetName,
                                translations: {
                                    ...commonWidgetConfig.translations,
                                    widgetNamespace:
                                        TRANSLATION_NAMESPACES.hubWidgetV2,
                                },
                            },
                            this.config.logger
                        );
                    }

                    await widget.init();

                    this.config.eventsCollector.pushEvent(
                        this.config.eventsFactory.createWidgetInjected(
                            widgetName
                        )
                    );

                    dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
                })();
            });
    }
}

export default RewardsHubWidgetInjector;
